import React, { useEffect, useState } from 'react';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FORMAT_TEXT_COMMAND, $getSelection, $isRangeSelection, $isTextNode } from 'lexical';

import { TEXT_COLORS, HIGHLIGHT_COLORS, FONT_SIZES } from '../utils/toolConstants';
import {
    registerTextColorCommands,
    SET_TEXT_COLOR_COMMAND,
    SET_HIGHLIGHT_COLOR_COMMAND,
} from './TextColorPlugin';
import { registerFontSizeCommand, SET_FONT_SIZE_COMMAND } from './FontPlugin';
import { registerClearFormatting, CLEAR_FORMATTING_COMMAND } from './ClearFormattingPlugin';
import { applyAlignment } from './useAlignment';
import { updateLatexNodeColor } from './updateLatexNodeColor';
import InsertLatexForm from './InsertLatexForm';

interface ToolbarPluginProps {
    setEditorFocused: (focused: boolean) => void;
    simpleMode?: boolean;
}

export default function ToolbarPlugin({
    setEditorFocused,
    simpleMode,
}: ToolbarPluginProps): React.JSX.Element {
    const [editor] = useLexicalComposerContext();
    const [textColor, setTextColor] = useState('#000000');
    const [highlightColor, setHighlightColor] = useState('#ffffff');
    const [fontSize, setFontSize] = useState('12pt');

    useEffect(() => {
        registerTextColorCommands(editor);
        registerFontSizeCommand(editor);
        registerClearFormatting(editor);
    }, [editor]);

    const formatText = (style: 'bold' | 'italic' | 'underline') => {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, style);
    };

    const applyTextColor = (color: string) => {
        // Try to update LatexNode color, if any selected; otherwise, apply text color
        const didUpdateLatex = updateLatexNodeColor(editor, color);
        if (!didUpdateLatex) {
            editor.dispatchCommand(SET_TEXT_COLOR_COMMAND, color);
        }
    };

    const applyHighlightColor = (color: string) => {
        editor.dispatchCommand(SET_HIGHLIGHT_COLOR_COMMAND, color);
    };

    const applyFontSize = (size: string) => {
        editor.dispatchCommand(SET_FONT_SIZE_COMMAND, size);
    };

    return (
        <>
            <div
                className="toolbar"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    alignItems: 'center',
                    marginBottom: '1rem',
                }}
            >
                {!simpleMode && (
                    <>
                        {/* Inline formatting */}
                        <button
                            type="button"
                            onClick={() => formatText('bold')}
                            title="Bold (Ctrl+B)"
                        >
                            B
                        </button>
                        <button
                            type="button"
                            onClick={() => formatText('italic')}
                            title="Italic (Ctrl+I)"
                        >
                            I
                        </button>
                        <button
                            type="button"
                            onClick={() => formatText('underline')}
                            title="Underline (Ctrl+U)"
                        >
                            U
                        </button>

                        {/* Text color */}
                        <label className="toolbar-label">
                            Color:
                            <select
                                value={textColor}
                                onChange={(e) => {
                                    setTextColor(e.target.value);
                                    applyTextColor(e.target.value);
                                }}
                            >
                                <option value="" disabled>
                                    Select
                                </option>
                                {TEXT_COLORS.map(({ label, value }) => (
                                    <option key={`${label}-color`} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {/* Highlight */}
                        <label className="toolbar-label">
                            BG:
                            <select
                                value={highlightColor}
                                onChange={(e) => {
                                    setHighlightColor(e.target.value);
                                    applyHighlightColor(e.target.value);
                                }}
                            >
                                <option value="" disabled>
                                    Select
                                </option>
                                {HIGHLIGHT_COLORS.map(({ label, value }) => (
                                    <option key={`${label}-bg-color`} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {/* Font size */}
                        <label className="toolbar-label">
                            Size:
                            <select
                                value={fontSize}
                                onChange={(e) => {
                                    setFontSize(e.target.value);
                                    applyFontSize(e.target.value);
                                }}
                            >
                                <option value="" disabled>
                                    Select
                                </option>
                                {FONT_SIZES.map((size) => (
                                    <option key={`${size}-size`} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {/* Clear formatting */}
                        <button
                            type="button"
                            onClick={() =>
                                editor.dispatchCommand(CLEAR_FORMATTING_COMMAND, undefined)
                            }
                            title="Clear Formatting"
                        >
                            Clear
                        </button>

                        {/* Alignment */}
                        <div className="toolbar-group">
                            <button
                                type="button"
                                onClick={() => applyAlignment(editor, 'left')}
                                title="Align Left"
                            >
                                L
                            </button>
                            <button
                                type="button"
                                onClick={() => applyAlignment(editor, 'center')}
                                title="Align Center"
                            >
                                C
                            </button>
                            <button
                                type="button"
                                onClick={() => applyAlignment(editor, 'right')}
                                title="Align Right"
                            >
                                R
                            </button>
                            <button
                                type="button"
                                onClick={() => applyAlignment(editor, 'justify')}
                                title="Justify"
                            >
                                J
                            </button>
                        </div>
                    </>
                )}

                {/* Insert LaTeX */}
                <InsertLatexForm simpleMode={simpleMode} />

                <span style={{ flex: 1 }} />
                <button
                    type="button"
                    onClick={() => setEditorFocused(false)}
                    style={{
                        color: 'red',
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        marginLeft: 'auto',
                    }}
                    title="Close toolbar"
                >
                    &times;
                </button>
            </div>

            {/* Selection syncing for dropdown states */}
            <OnChangePlugin
                onChange={(editorState) => {
                    editorState.read(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            const nodes = selection.getNodes();
                            for (const node of nodes) {
                                if ($isTextNode(node)) {
                                    const style = node.getStyle();
                                    const colorMatch =
                                        /color:\s*(#[0-9a-fA-F]{3,6}|[a-zA-Z]+)/.exec(style ?? '');
                                    const bgMatch =
                                        /background-color:\s*(#[0-9a-fA-F]{3,6}|[a-zA-Z]+)/.exec(
                                            style ?? ''
                                        );
                                    setTextColor(colorMatch?.[1] ?? TEXT_COLORS[0].value);
                                    setHighlightColor(bgMatch?.[1] ?? HIGHLIGHT_COLORS[0].value);
                                    break;
                                }
                            }
                        }
                    });
                }}
            />
        </>
    );
}
